import React, { useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
} from "reactstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import moment from "moment";
import { getBulkDelivery, getBulkSchedule } from "store/bulk-lists/actions";
import TableContainer from "../../components/Common/TableContainer";

const fmtDate = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "—");
const fmtNum = (n) =>
  n == null || n === "" ? "—" : Number(n).toLocaleString();

const MessageTimeline = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const dispatch = useDispatch();

  // Campaign row passed through router state by the Outbox; bulkSchedule + the
  // first delivery row are fallbacks on a direct load/refresh.
  const campaign = state?.campaign;

  const CombinedProperties = createSelector(
    (s) => s.BulkList,
    (s) => s.User,
    (bulkList, user) => ({
      bulkSchedule: bulkList.bulkSchedule,
      bulkDeliveryList: bulkList.bulkDeliveryList,
      userProfile: user.userProfile,
    })
  );

  const { bulkSchedule, bulkDeliveryList, userProfile } =
    useSelector(CombinedProperties);

  const deliveryRows = bulkDeliveryList?.data || [];
  const firstRow = deliveryRows[0] || {};

  // Header values, with graceful fallbacks.
  const sentBy =
    userProfile?.companies?.[0]?.name ||
    userProfile?.User?.name ||
    "—";
  const sender = campaign?.shortcode || bulkSchedule?.shortcode || firstRow.from || "—";
  const lists = (campaign?.lists || [])
    .map((l) => l.name)
    .filter(Boolean)
    .join(", ");
  const sentTo = lists || bulkSchedule?.contact_list || "—";
  const sentDate = campaign?.send_date_time || bulkSchedule?.send_date_time;
  const message =
    campaign?.message || firstRow.message || bulkSchedule?.message || "—";
  const contacts = campaign?.send_count ?? bulkSchedule?.send_count;
  const units = campaign?.unit_consumed;

  const columns = [
    { header: "#", accessorKey: "ID", enableColumnFilter: false },
    { header: "From", accessorKey: "from", enableColumnFilter: false },
    { header: "To", accessorKey: "to", enableColumnFilter: false },
    { header: "Message", accessorKey: "message", enableColumnFilter: false },
    {
      header: "Date",
      accessorKey: "created_at",
      enableColumnFilter: false,
      cell: (info) => (info.getValue() ? fmtDate(info.getValue()) : "—"),
    },
    {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: false,
      cell: (info) => {
        const delivered = info.getValue() === "0";
        return (
          <Badge color={delivered ? "success" : "secondary"} className="font-size-12">
            {delivered ? "Delivered" : "Not Delivered"}
          </Badge>
        );
      },
    },
  ];

  useEffect(() => {
    dispatch(getBulkSchedule(id));
    dispatch(getBulkDelivery({ schedule_sms_id: id, page: 0, pageSize: 30 }));
  }, [id]);

  const HeaderItem = ({ label, children }) => (
    <Col md={4} lg={3} className="mb-3">
      <p className="text-muted text-uppercase font-size-11 mb-1">{label}</p>
      <div className="fw-medium">{children}</div>
    </Col>
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Message Detail</h5>
                  <Button color="primary" outline onClick={() => navigate("/outbox")}>
                    <i className="mdi mdi-arrow-left font-size-16 align-middle me-1"></i> Back
                  </Button>
                </div>

                <Row>
                  <HeaderItem label="Sent by">{sentBy}</HeaderItem>
                  <HeaderItem label="From">{sender}</HeaderItem>
                  <HeaderItem label="Sent to">{sentTo}</HeaderItem>
                  <HeaderItem label="Date sent">{fmtDate(sentDate)}</HeaderItem>
                  <HeaderItem label="Contacts">{fmtNum(contacts)}</HeaderItem>
                  <HeaderItem label="Units">{fmtNum(units)}</HeaderItem>
                  <HeaderItem label="Status">
                    <span className="text-success">Sent</span>
                  </HeaderItem>
                </Row>

                <hr />
                <p className="text-muted text-uppercase font-size-11 mb-1">Message</p>
                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{message}</p>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Delivery Reports</h4>
                <TableContainer
                  columns={columns}
                  data={deliveryRows}
                  isGlobalFilter={true}
                  isPagination={true}
                  isCustomPageSize={true}
                  pageSizeAtBottom={true}
                  SearchPlaceholder="Search delivery reports..."
                  pagination="pagination"
                  paginationWrapper="dataTables_paginate paging_simple_numbers"
                  tableClass="table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default MessageTimeline;
